import { asadoSdk } from '@fff-web/asado-sdk';
import env from './env';

const getAsadoSdk = () =>
  asadoSdk(env.env, true, env.asado, {
    platform: 'web-legacy',
    version: `plutus@${process.env.APP_VERSION ?? 'unknown'}`,
  });

const handleUnauthorizedReq = () => {
  window.location = `${env.loginUrl}?origin=${window.location.href}`;
};

const handleInvalidBoxID = () => {
  console.error('Invalid BoxID');
  window.location = '/';
};

// makes an API call and returns a JSON object
const sendRequest = (
  path,
  method = 'GET',
  body,
  additionalHeaders = {},
  json = true,
  cache = 'default',
) => {
  const defaultHeaders = {
    'FFF-Auth': 'V1.1',
    Origin: env.originURL,
    'Content-Type': 'application/json; charset=utf-8',
  };
  return fetch(path, {
    credentials: 'include',
    headers: { ...defaultHeaders, ...additionalHeaders },
    data: {
      activatedOn: true,
    },
    body: method === 'POST' || method === 'PUT' || method === 'PATCH' ? JSON.stringify(body) : body,
    mode: 'cors',
    method,
    cache,
  })
    .then((response) => {
      const code = response.code || response.status;
      const message = response.message ? response.message : '';
      if (code === 401) return handleUnauthorizedReq();
      if (code === 404 && message === 'Invalid BoxID') return handleInvalidBoxID();
      return response;
    })
    .then((response) => json && response.json())
    .catch((err) => {
      console.error('Fetch Failed: ', err);
      return { error: err, success: false };
    });
};

export const checkAuth = () => sendRequest(env.loginVerify);

/** BFF upstreams whose failure invalidates the menu-data fields plutus reads. */
const MENU_DATA_CRITICAL_SOURCES = ['growth', 'user'];

/**
 * Reads the authenticated member's menu/profile data from the asado BFF
 * (`GET /bff/v1/user/menu-data`).
 *
 * Throws — rather than resolving to `null` — when the payload is unavailable. Menu-data gates the
 * expired/club/refill branches of My Box (`boxExpired`, `isClubMember`) and the `Refills Cancelled`
 * analytics traits, and the BFF reports `null` subscription fields for legitimate non-box members
 * (club/social/refill/none). A failed read is therefore indistinguishable from a real member once
 * merged, and silently degrades to "expired customer" — so it must fail loudly instead.
 *
 * A degraded 200 (HTTP 200 carrying `errors[]` plus partial fields) counts as a failed read only
 * when a *critical* upstream failed. `growth` feeds `subscriptionState`; `user` feeds `tier`,
 * `subscriptionType`, `role` and `shippingCountry`. A `hermes` (avatar) failure leaves every field
 * plutus reads fully populated, so it is tolerated — plutus does not render the avatar. This is
 * narrower than maui's `getMenuDataAction` and `@fff-web/segment`, which reject any `errors[]`.
 * @returns {Promise<object>} The menu-data `user` payload.
 */
export const getMenuData = async () => {
  const response = await getAsadoSdk().getMenuData();

  if (!response.ok || !response.data?.data?.user) {
    throw new Error(`menu-data unavailable (status: ${response.status})`);
  }

  const criticalFailures = (response.data.errors ?? []).filter((error) =>
    MENU_DATA_CRITICAL_SOURCES.includes(error.source),
  );

  if (criticalFailures.length) {
    const sources = criticalFailures.map((error) => error.source).join(', ');
    throw new Error(`menu-data returned a degraded envelope (failed upstreams: ${sources})`);
  }

  return response.data.data.user;
};

export const getBoxData = () => sendRequest(`${env.box}/self`, 'GET', null, {}, true, 'no-store');

export const request = (method, url, headers = {}, body = null) => {
  let options = { method, headers: new Headers(headers) };
  if (body) options = Object.assign({}, options, { body: JSON.stringify(body) });
  const req = new Request(url, options);
  return req;
};

export const saveRevealState = (sku, value) =>
  sendRequest(`${env.box}/sku/${sku}/reveal`, 'PUT', {
    reveal: value,
  });

export const getSpecData = (id) => sendRequest(`${env.erosURL}${id}.json`);

/**
 * Returns a Request object that we can use to make API requests
 * @param {string} method - The HTTP method to use
 * @param {string} path - The path to the API endpoint
 * @param {object} body - The body of the request
 * @returns {Request} - The Request object
 */
export function apiRequest(method = 'GET', path, body) {
  let headers = {
    'FFF-Auth': 'V1.1',
  };

  if (method === 'POST' || method === 'PUT' || method === 'DELETE' || method === 'PATCH') {
    headers = {
      ...headers,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
  }

  const options = {
    method,
    credentials: 'include',
    headers: new Headers(headers),
  };

  if ((method === 'PUT' || method === 'POST' || method === 'DELETE' || method === 'PATCH') && body)
    options.body = JSON.stringify(body);
  return new Request(path, options);
}

export const getUserBoxReview = () =>
  sendRequest(`${env.questionnaire}/ratings/self`, 'GET', null, {}, true, 'no-store');

export const getSaleProducts = (saleId) =>
  sendRequest(
    `${env.storefront}/api/1/sales/${saleId}/products?countryIsoCode=US&limit=999&offset=0&orderBy=best_selling&preview=false`,
    'POST',
    null,
    {},
    true,
    'no-store',
  );

export const getCustomizeSaleInfo = (customizeSaleId, countryIsoCode = 'US') =>
  sendRequest(
    `${env.storefront}/api/1/sales/${customizeSaleId}/customize-sale-info?countryIsoCode=${countryIsoCode}&isPublic=false&preview=false`,
    'GET',
    null,
    {},
    true,
    'no-store',
  );

export const processBoxSubscription = (cartId, saleUuid, data) =>
  sendRequest(
    `${env.ace}api/1/carts/${cartId}/update?checkoutCustomizeSaleUuid=${saleUuid}`,
    'PATCH',
    data,
    {},
    true,
    'no-store',
  );

/**
 * Creates a new cart for the authenticated user. Used by the INSTANT-cart
 * checkout flow — the backend responds with the created cart's uuid, which the
 * caller redirects to via maui's `/confirm/checkout?cartUuid=…&cartSource=…`.
 * @param {'INSTANT' | 'CUSTOMER' | 'SUBSCRIPTION' | 'CUSTOMIZE'} sourceType - Cart source type.
 * @param {Array} cartItems - Cart items in maui's `NewCartItem` shape.
 * @returns {Promise} - The ACE create-cart response: `{ data: { items: CartData[] }, error? }`.
 */
export const createUserCart = (sourceType, cartItems) =>
  sendRequest(
    `${env.ace}api/1/carts`,
    'POST',
    { cartItems, preview: false, sourceType },
    { Accept: 'application/vnd.fabfitfun.v2+json;charset=UTF-8' },
    true,
    'no-store',
  );

/**
 * Get user cart data
 * @returns {Promise} - The cart data response. Each cart is normalized so
 *   `prepaidSubscriptionGroups` is always an array, even if the backend omits it.
 */
export const getUserCartData = async () => {
  const queryParams = new URLSearchParams({
    checkedOut: false,
    timerId: 0,
    sourceType: 'CUSTOMIZE',
  });

  const url = `${env.ace}api/1/carts?${queryParams.toString()}`;

  const response = await sendRequest(url, 'GET', null, {}, true, 'no-store');

  if (Array.isArray(response)) {
    return response.map((cart) => ({
      ...cart,
      prepaidSubscriptionGroups: cart?.prepaidSubscriptionGroups ?? [],
    }));
  }

  return response;
};
